import { z } from "zod";

const translationPath = "page.evaluation.forms.errors.";
const typeFormSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "typeForm" },
);
const layoutFormSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "layoutForm" },
);
const answeredFormSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "answeredForm" },
);
const typeSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "type" },
);

const questionSchema = z.object({
  id: z.string(),
  title: z.string().min(1, translationPath + "title"),
  type: typeSchema.optional(),
  displayOrder: z.string(),
});

const siteAppExtensionSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "site_app_extension" },
);

export const formSchema = z
  .object({
    id: z.string().optional(),
    enable: z.boolean(),
    title: z.string().min(1, translationPath + "title"),
    integrationId: z.string().optional(),
    description: z.string().min(1, translationPath + "description"),
    typeForm: typeFormSchema,
    layoutForm: layoutFormSchema,
    answeredForm: answeredFormSchema,
    visualDescription: z
      .string()
      .min(1, translationPath + "visual_description"),
    evaluationFormQuestions: z
      .array(questionSchema)
      .min(1, translationPath + "questions"),
    siteAppExtension: siteAppExtensionSchema,
    showLinkOnEnd: z.boolean(),
    showLinkGreaterThen: z.string().nullish(),
    responseTimeLimitHours: z.string().nullish(),
    showLinkLink: z.string().nullish(),
    showLinkDescription: z.string().nullish(),
    showLinkButtonLabel: z.string().nullish(),
    initialDate: z.date({
      invalid_type_error: translationPath + "initial_date",
    }),
    endDate: z.date({ invalid_type_error: translationPath + "end_date" }),
  })
  .superRefine((val, ctx) => {
    if (val.evaluationFormQuestions.some((v) => !v.displayOrder)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: translationPath + "order",
        path: ["evaluationFormQuestions"],
      });
    }
    const displayOrderCounts = val.evaluationFormQuestions.reduce(
      (acc: any, curr) => {
        acc[curr.displayOrder] = (acc[curr.displayOrder] || 0) + 1;
        return acc;
      },
      {},
    );

    const hasDuplicates = Object.values(displayOrderCounts).some(
      (count: any) => count > 1,
    );

    if (hasDuplicates) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: translationPath + "duplicate_order",
        path: ["evaluationFormQuestions"],
      });
    }

    if (val.initialDate > val.endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: translationPath + "bigger_date",
        path: ["initialDate"],
      });
    }

    if (val.responseTimeLimitHours) {
      const responseTimeLimitHours = Number(val.responseTimeLimitHours);

      if (
        !Number.isInteger(responseTimeLimitHours) ||
        responseTimeLimitHours <= 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translationPath + "response_time_limit_hours",
          path: ["responseTimeLimitHours"],
        });
      }
    }

    if (val.showLinkOnEnd) {
      if (!val.showLinkGreaterThen) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translationPath + "show_link_greater_than",
          path: ["showLinkGreaterThen"],
        });
      }

      if (!val.showLinkLink) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translationPath + "show_link_link",
          path: ["showLinkLink"],
        });
      }

      if (!val.showLinkDescription) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translationPath + "show_link_description",
          path: ["showLinkDescription"],
        });
      }

      if (!val.showLinkButtonLabel) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: translationPath + "show_link_button_label",
          path: ["showLinkButtonLabel"],
        });
      }
    }
  });

export type TFormsForm = z.infer<typeof formSchema>;
